import { default as MdiIcon } from "@mdi/react";
import { useHotkeys } from "react-hotkeys-hook";

import { mdiArrowLeftBold, mdiArrowRightBold, mdiRefresh } from "@mdi/js";
import { Input, Slider, Switch, Typography } from "antd";
import moment from "moment";
import styled from "styled-components";

import { useUserEvents } from "hooks";

import { Uwi } from "models";

import { DraggableLegendBase } from "components/base/DraggableLegendBase";
import { IconButton } from "components/pad-generator/ScenarioConfigurationTable";

import { useNetworkVisualizationAdapter } from "./hooks/adapters/useNetworkVisualizationAdapter";

// ── Constants ────────────────────────────────────────────────────────────────

const NETWORK_OPTIONS_DIMENSIONS = { width: 280, height: 235 };

const { Title } = Typography;

// ── Main Component ───────────────────────────────────────────────────────────

export function NetworkVisualizationOverlay(): JSX.Element {
  const {
    // State
    isActive,
    networkLineWidth,
    wellNetworkMap,
    networkDepthLegend,
    wellNetworkMapRequest,
    containerRef,
    containerWidth,
    containerHeight,

    // Actions
    clearNetworkMap,
    updateWellNetworkMapRequest,
    setNetworkLineWidth
  } = useNetworkVisualizationAdapter();

  if (!isActive) {
    return null;
  }

  const parentDimensions =
    containerWidth && containerHeight
      ? { width: containerWidth, height: containerHeight }
      : {
          width:
            (containerRef as React.RefObject<HTMLDivElement>).current?.clientWidth ??
            window.innerWidth,
          height:
            (containerRef as React.RefObject<HTMLDivElement>).current?.clientHeight ??
            window.innerHeight
        };

  return (
    <Root ref={containerRef} data-testid="network-visualization-overlay">
      {isActive && (
        <DraggableLegendBase
          visible={true}
          closeable
          initialWidth={NETWORK_OPTIONS_DIMENSIONS.width}
          initialHeight={NETWORK_OPTIONS_DIMENSIONS.height}
          onClose={clearNetworkMap}
          parentDimensions={parentDimensions}
          legendTitle={<Title level={5}>Network Options</Title>}>
          <NetworkMapOptions
            onClose={clearNetworkMap}
            showOnlyPrimaryPath={wellNetworkMapRequest.onlyPrimaryPath}
            width={networkLineWidth as [number, number]}
            wellIds={wellNetworkMapRequest.wellIds}
            onChange={(value) => {
              updateWellNetworkMapRequest({
                onlyPrimaryPath: value.showOnlyPrimaryPath,
                date: value.date,
                hideConnectionsLessThanPercent: value.hideConnectionsLessThanPercent
              });
            }}
            date={wellNetworkMapRequest.date ?? wellNetworkMap?.date}
            startDate={wellNetworkMap?.startDate}
            endDate={wellNetworkMap?.endDate}
            hideConnectionsLessThanPercent={
              wellNetworkMapRequest.hideConnectionsLessThanPercent
            }
            onWidthChange={(value) => {
              setNetworkLineWidth(value);
            }}
          />
        </DraggableLegendBase>
      )}
      {isActive && (
        <DraggableLegendBase
          visible={networkDepthLegend.visible}
          closeable
          key={`depth-legend-${networkDepthLegend.visible ? "visible" : "hidden"}-${
            wellNetworkMapRequest.wellIds?.join(",") || "no-wells"
          }`}
          yOffset={NETWORK_OPTIONS_DIMENSIONS.height + 20}
          onClose={clearNetworkMap}
          parentDimensions={parentDimensions}
          legendTitle={<Title level={5}>{networkDepthLegend.title}</Title>}>
          {networkDepthLegend.legendItems.map((item) => (
            <div key={item.title} style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  backgroundColor: item.color,
                  marginRight: "5px",
                  border: "1px solid #ccc",
                  borderRadius: "2px"
                }}
              />
              <span style={{ fontSize: "14px" }}>{item.title}</span>
            </div>
          ))}
        </DraggableLegendBase>
      )}
    </Root>
  );
}

// ── NetworkMapOptions ────────────────────────────────────────────────────────

interface NetworkMapSettings {
  showOnlyPrimaryPath?: boolean;
  minFraction?: number;
  date?: string;
  startDate?: Date;
  endDate?: Date;
  width: [number, number];
  onChange?: (settings: NetworkMapSettings) => void;
  onWidthChange?: (width: [number, number]) => void;
  onClose: () => void;
  hideConnectionsLessThanPercent?: number;
  wellIds?: string[];
}

function NetworkMapOptions(options: NetworkMapSettings): JSX.Element {
  const { sendWellNetworkShowingEvent } = useUserEvents();

  const canGoToPreviousMonth = () =>
    options.startDate &&
    options.date &&
    moment(options.date, "YYYY-MM-DD") > moment(options.startDate);

  const canGoToNextMonth = () =>
    options.endDate &&
    options.date &&
    moment(options.date, "YYYY-MM-DD") < moment(options.endDate);

  const goToPreviousMonth = (keyboardEvent?: KeyboardEvent) => {
    keyboardEvent?.preventDefault?.();
    keyboardEvent?.stopPropagation?.();
    const currentMonth = options.date;
    if (!currentMonth || !canGoToPreviousMonth()) {
      return;
    }
    const date = moment(currentMonth, "YYYY-MM-DD");
    date.subtract(1, "months");

    const newDate = date.format("YYYY-MM-DD");
    options.onChange?.({ ...options, date: newDate });

    const monthsFromCurrentDay = moment().diff(moment(newDate, "YYYY-MM-DD"), "months");

    sendWellNetworkShowingEvent({
      source: "Going to Previous Month",
      selectedWell: options.wellIds?.[0] || "",
      primaryPathToggle: options.showOnlyPrimaryPath,
      date: newDate,
      monthsFromCurrentDay,
      contributionPercentage: options.hideConnectionsLessThanPercent || 0,
      width: options.width
    });
  };

  const goToNextMonth = (keyboardEvent?: KeyboardEvent) => {
    keyboardEvent?.preventDefault?.();
    keyboardEvent?.stopPropagation?.();
    const currentMonth = options.date;
    if (!currentMonth || !canGoToNextMonth()) {
      return;
    }
    const date = moment(currentMonth, "YYYY-MM-DD");
    date.add(1, "months");

    const newDate = date.format("YYYY-MM-DD");
    options.onChange?.({ ...options, date: newDate });

    const monthsFromCurrentDay = moment().diff(moment(newDate, "YYYY-MM-DD"), "months");

    sendWellNetworkShowingEvent({
      source: "Going to Next Month",
      selectedWell: options.wellIds?.[0] || "",
      primaryPathToggle: options.showOnlyPrimaryPath,
      date: newDate,
      monthsFromCurrentDay,
      contributionPercentage: options.hideConnectionsLessThanPercent || 0,
      width: options.width
    });
  };

  useHotkeys("left", goToPreviousMonth, { keyup: true }, [
    options.date,
    options.startDate
  ]);
  useHotkeys("right", goToNextMonth, { keyup: true }, [options]);

  return (
    <NetworkMapOptionsRoot>
      {options.wellIds && options.wellIds.length > 0 && (
        <OptionItem>
          <label>Well UWI</label>
          <div>{options.wellIds.map((uwi) => new Uwi().toFormatted(uwi)).join(", ")}</div>
        </OptionItem>
      )}
      <OptionItem>
        <label htmlFor={"primary-path-opt"}>Primary Path</label>
        <Switch
          checked={options.showOnlyPrimaryPath}
          id={"primary-path-opt"}
          onChange={(checked) => {
            options.onChange?.({ ...options, showOnlyPrimaryPath: checked });

            sendWellNetworkShowingEvent({
              source: "Toggling Primary Path",
              selectedWell: options.wellIds?.[0] || "",
              primaryPathToggle: checked,
              date: options.date,
              contributionPercentage: options.hideConnectionsLessThanPercent || 0,
              width: options.width
            });
          }}
        />
      </OptionItem>

      <OptionItem>
        <label>
          Month
          <IconButton
            title={"Reset"}
            type={"text"}
            shape={"circle"}
            icon={<MdiIcon path={mdiRefresh} size={1.0} />}
            onClick={() => options.onChange?.({ ...options, date: null })}
          />
        </label>
        <MonthSelection style={{ minWidth: "100px" }}>
          <IconButton
            title={"Previous Month"}
            icon={<MdiIcon path={mdiArrowLeftBold} size={1.0} />}
            type={"text"}
            disabled={!canGoToPreviousMonth()}
            shape={"circle"}
            onClick={goToPreviousMonth}
          />
          <div className={"current-month-showing"}>
            {options.date ? moment(options.date, "YYYY-MM-DD").format("YYYY-MM") : ""}
          </div>
          <IconButton
            title={"Next Month"}
            icon={<MdiIcon path={mdiArrowRightBold} size={1.0} />}
            type={"text"}
            disabled={!canGoToNextMonth()}
            shape={"circle"}
            onClick={goToNextMonth}
          />
        </MonthSelection>
      </OptionItem>
      <OptionItem>
        <label style={{ minWidth: "150px" }}>Min Contribution (%)</label>
        <Input
          type={"number"}
          placeholder={"< %"}
          value={options.hideConnectionsLessThanPercent}
          onChange={(e) => {
            const contributionPercentage = parseInt(e.target.value);
            options.onChange?.({
              ...options,
              hideConnectionsLessThanPercent:
                contributionPercentage > 100 ? 100 : contributionPercentage || 0
            });
          }}
          onBlur={() => {
            sendWellNetworkShowingEvent({
              source: "Changing Min Contribution Percentage",
              selectedWell: options.wellIds?.[0] || "",
              primaryPathToggle: options.showOnlyPrimaryPath,
              date: options.date,
              contributionPercentage: options.hideConnectionsLessThanPercent || 0,
              width: options.width
            });
          }}
          min={0}
          max={100}
        />
      </OptionItem>
      <OptionItem>
        <label>Width</label>
        <Slider
          min={1}
          max={20}
          value={options.width}
          range={true}
          onChange={(value) => options.onWidthChange?.(value)}
        />
      </OptionItem>
    </NetworkMapOptionsRoot>
  );
}

// ── Styled Components ────────────────────────────────────────────────────────

const OptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
  label {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .ant-slider {
    min-width: 100px;
  }
`;

const MonthSelection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  .current-month-showing {
    text-align: center;
    width: 80px;
  }
`;

const NetworkMapOptionsRoot = styled.div`
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  pointer-events: auto;
  z-index: 10;

  .ant-card-body {
    padding: 0;
    width: 100%;
  }
`;

const Root = styled.div`
  position: absolute;
  display: block;
  user-select: none;
  z-index: 1;
  pointer-events: none;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;
